import { inject, type App, type InjectionKey } from 'vue';
import { HocuspocusProvider, HocuspocusProviderWebsocket } from '@hocuspocus/provider';
import type { Awareness } from 'y-protocols/awareness';
import type * as Y from 'yjs';

/** Where the description editor syncs (same origin and session cookie as the API). */
const COLLAB_PATH = '/api/v1/collab';

/** One open shared document (a description), as the editor needs it. */
export interface CollabDoc {
  readonly awareness: Awareness | null;
  /** Share part of this user's presence (a cursor, a selection). */
  setField(field: string, value: unknown): void;
  /** Called when the first full sync arrives, and whether this user may edit. */
  onSynced(listener: (canEdit: boolean) => void): void;
  /** Connection state, for a quiet "reconnecting" note. */
  onStatus(listener: (connected: boolean) => void): void;
  /** The server refused (no access, or access was lost). */
  onDenied(listener: () => void): void;
  close(): void;
}

/** What components need from the collaboration connection (tests provide a fake). */
export interface Collab {
  open(itemId: string, doc: Y.Doc): CollabDoc;
  /** A whiteboard's drawing (the same connection). */
  openBoard(boardId: string, doc: Y.Doc): CollabDoc;
  /** A doc page's text (the same connection). */
  openPage(pageId: string, doc: Y.Doc): CollabDoc;
  /** One of a decision's two documents: its question's details, or the decision. */
  openDecision(field: 'question' | 'decision', decisionId: string, doc: Y.Doc): CollabDoc;
  install(app: App): void;
}

/**
 * The description editor's connection: one websocket per tab (opened with the
 * first document), carrying every open description. Edits made while it's
 * reconnecting are kept by Yjs and sent once it's back.
 */
export class CollabClient implements Collab {
  #socket: HocuspocusProviderWebsocket | null = null;

  #websocket(): HocuspocusProviderWebsocket {
    if (!this.#socket) {
      const scheme = globalThis.location.protocol === 'https:' ? 'wss' : 'ws';
      this.#socket = new HocuspocusProviderWebsocket({
        url: `${scheme}://${globalThis.location.host}${COLLAB_PATH}`,
      });
    }
    return this.#socket;
  }

  open(itemId: string, doc: Y.Doc): CollabDoc {
    return this.#open(`item:${itemId}`, doc);
  }

  openBoard(boardId: string, doc: Y.Doc): CollabDoc {
    return this.#open(`board:${boardId}`, doc);
  }

  openPage(pageId: string, doc: Y.Doc): CollabDoc {
    return this.#open(`page:${pageId}`, doc);
  }

  openDecision(field: 'question' | 'decision', decisionId: string, doc: Y.Doc): CollabDoc {
    return this.#open(`${field}:${decisionId}`, doc);
  }

  #open(name: string, doc: Y.Doc): CollabDoc {
    const synced: ((canEdit: boolean) => void)[] = [];
    const status: ((connected: boolean) => void)[] = [];
    const denied: (() => void)[] = [];
    const provider = new HocuspocusProvider({
      websocketProvider: this.#websocket(),
      name,
      document: doc,
      // The session cookie authenticates; the server ignores this value.
      token: 'session',
      onSynced: () => {
        const canEdit = provider.authorizedScope !== 'readonly';
        for (const l of synced) l(canEdit);
      },
      onStatus: ({ status: s }) => {
        for (const l of status) l(s === 'connected');
      },
      onAuthenticationFailed: () => {
        for (const l of denied) l();
      },
    });
    provider.attach();
    return {
      awareness: provider.awareness,
      setField: (field, value) => provider.setAwarenessField(field, value),
      onSynced: (l) => synced.push(l),
      onStatus: (l) => status.push(l),
      onDenied: (l) => denied.push(l),
      close: () => provider.destroy(),
    };
  }

  /** After signing out: drop the connection (the next session opens a new one). */
  disconnect(): void {
    this.#socket?.destroy();
    this.#socket = null;
  }

  install(app: App): void {
    app.provide(COLLAB_KEY, this);
  }
}

export const COLLAB_KEY: InjectionKey<Collab> = Symbol('dometry-collab');

export function useCollab(): Collab {
  const collab = inject(COLLAB_KEY, null);
  if (!collab) throw new Error('useCollab() called without the collaboration plugin installed');
  return collab;
}
